// LRPO-3181 — Client-side table pagination.
// Slices a table's <tbody> rows into pages, renders a range label and a
// windowed set of pager buttons, and stays in sync as rows are added or
// removed.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["perPage", "range", "pager"]
  static values = { perPage: { type: Number, default: 20 } }

  connect() {
    this.rows = this.collectRows()
    this.page = 1

    this.tbody = this.element.querySelector("tbody")
    this.observer = new MutationObserver(() => this.onRowsChanged())
    if (this.tbody) {
      this.observer.observe(this.tbody, { childList: true })
    }

    this.apply()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  // Re-collect rows after a childList mutation, clamp the page to the new
  // page count (min 1), and re-apply. Setting row.hidden is an attribute
  // change, not a childList change, so this does not re-trigger the observer.
  onRowsChanged() {
    this.rows = this.collectRows()
    const maxPage = this.pageCount
    this.page = Math.min(Math.max(this.page, 1), maxPage)
    this.apply()
  }

  changePerPage() {
    const value = parseInt(this.perPageTarget.value, 10)
    if (Number.isFinite(value) && value > 0) {
      this.perPageValue = value
    }
    this.page = 1
    this.apply()
  }

  goTo(n) {
    const page = Math.min(Math.max(n, 1), this.pageCount)
    if (page === this.page) {
      return
    }
    this.page = page
    this.apply()
  }

  prev() {
    this.goTo(this.page - 1)
  }

  next() {
    this.goTo(this.page + 1)
  }

  collectRows() {
    return Array.from(this.element.querySelectorAll("tbody tr"))
  }

  get pageCount() {
    if (this.rows.length === 0) {
      return 1
    }
    return Math.ceil(this.rows.length / this.perPageValue)
  }

  apply() {
    const perPage = this.perPageValue
    const total = this.rows.length
    const start = (this.page - 1) * perPage
    const end = Math.min(start + perPage, total)

    this.rows.forEach((row, i) => {
      row.hidden = i < start || i >= end
    })

    this.rangeTarget.textContent = total === 0
      ? "0-0 of 0"
      : `${start + 1}-${end} of ${total}`

    this.renderPager()
  }

  renderPager() {
    const pageCount = this.pageCount
    const current = this.page
    const nodes = []

    nodes.push(this.pageButton("prev", "Prev", current === 1, () => this.prev()))

    this.pageWindow(pageCount, current).forEach((item) => {
      if (item === "ellipsis") {
        const span = document.createElement("span")
        span.setAttribute("data-table-pager-ellipsis", "")
        span.textContent = "…"
        nodes.push(span)
      } else {
        nodes.push(
          this.pageButton(String(item), String(item), false, () => this.goTo(item), item === current)
        )
      }
    })

    nodes.push(this.pageButton("next", "Next", current === pageCount, () => this.next()))

    this.pagerTarget.innerHTML = ""
    nodes.forEach((node) => this.pagerTarget.appendChild(node))
  }

  pageButton(page, label, disabled, onClick, active = false) {
    const button = document.createElement("button")
    button.type = "button"
    button.setAttribute("data-table-pager-page", page)
    button.textContent = label
    if (disabled) {
      button.disabled = true
    }
    button.classList.add("btn", "btn-sm")
    if (active) {
      button.classList.add("btn-primary", "active")
      button.setAttribute("aria-current", "page")
    } else {
      button.classList.add("btn-light", "border")
    }
    button.addEventListener("click", onClick)
    return button
  }

  // With ≤7 pages render all of them. Otherwise always render page 1 and the
  // last page plus current-1/current/current+1, collapsing each contiguous
  // gap into a single ellipsis marker.
  pageWindow(pageCount, current) {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, i) => i + 1)
    }

    const wanted = new Set([1, pageCount, current - 1, current, current + 1])
    const pages = []
    wanted.forEach((p) => {
      if (p >= 1 && p <= pageCount) {
        pages.push(p)
      }
    })
    pages.sort((a, b) => a - b)

    const items = []
    let previous = 0
    pages.forEach((p) => {
      if (p - previous > 1) {
        items.push("ellipsis")
      }
      items.push(p)
      previous = p
    })
    return items
  }
}
